// src/contexts/CartContext.js
import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { deriveStorageKey, loadCart, saveCart } from './cartStorage';
import { DEFAULT_PREFERENCES, optimizeCart } from '../utils/cartOptimizer';
import { variantKey, sameVariant } from '../utils/variantKey';
import { applyBuylistPriceRefusal } from '../utils/buylistRefusal';
import { foldBuyLadders } from '../utils/buylistTierLadder';
import { useKioskTheme } from './KioskThemeContext';
import { readOrderLimits, roomFor, orderLimitStatus } from '../utils/orderLimits';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const location = useLocation();
  const { currencyCode, themeOverrides } = useKioskTheme();
  // TKT-2026-105480. The store's order size limit, off unless the owner set one.
  // Read through a ref by the writers below, which are stable callbacks.
  const orderLimits = useMemo(() => readOrderLimits(themeOverrides), [themeOverrides]);
  const orderLimitsRef = useRef(orderLimits);
  useEffect(() => { orderLimitsRef.current = orderLimits; }, [orderLimits]);
  // Storage key follows the active store (first path segment), so `/brt` and
  // `/brt/cart` share one cart while different stores stay isolated.
  const storageKey = useMemo(() => deriveStorageKey(location.pathname), [location.pathname]);

  // Hydrate synchronously on first mount from the current store so a full-page
  // reload (iPad Safari tab reap, pull-to-refresh, ErrorBoundary reload)
  // restores the cart with no flash of an empty cart. (TKT-2026-103200)
  //
  // `storedCart` is what was added: a buy line keeps the engine's single-unit
  // offer and, on a store with reduction thresholds, its quantity ladder.
  // `cart` is what every screen, total and payload reads, with each buy line
  // priced per copy at its quantity on that ladder, the figure the buylist books
  // (TKT-2026-107051). Deriving it, rather than storing it, means a quantity
  // change re-walks from the engine's figures and never from a derived one.
  const [storedCart, setCart] = useState(() => loadCart(storageKey));
  const cart = useMemo(() => foldBuyLadders(storedCart, currencyCode), [storedCart, currencyCode]);
  const lastKeyRef = useRef(storageKey);
  const [kioskMode, setKioskMode] = useState('sell'); // 'sell' or 'buy'

  // TKT-2026-103353 idle auto-reset support. transactionInProgress blocks a reset
  // while a checkout is submitting or the collection-code screen is up; resetNonce
  // is bumped by requestReset() so screens can clear their own local UI state.
  const [transactionInProgress, setTransactionInProgress] = useState(false);
  const [resetNonce, setResetNonce] = useState(0);

  // TKT-2026-105225 cart optimisation. Which copy of a card the cart buys when the
  // store holds several: lowest price, foil or not, a minimum condition.
  //
  // Deliberately NOT persisted alongside the cart. A kiosk is a shared machine and
  // this is one shopper's stated preference, so it must not outlive their session
  // and reappear for the next person after a tab reload. The cart survives a
  // reload, the preference does not, which costs nothing: the picks it already
  // made are baked into the lines.
  const [cartPreferences, setCartPreferences] = useState(DEFAULT_PREFERENCES);

  // Re-hydrate when the active store changes mid-session (e.g. switching stores)
  // so one store's cart never bleeds into another.
  useEffect(() => {
    if (storageKey === lastKeyRef.current) return;
    lastKeyRef.current = storageKey;
    setCart(loadCart(storageKey));
    // A different store has different stock and its own optimisation permissions,
    // so a preference set at one kiosk must not carry over to another.
    setCartPreferences(DEFAULT_PREFERENCES);
  }, [storageKey]);

  // Persist on every change so the cart survives a reload.
  useEffect(() => {
    saveCart(storageKey, storedCart);
  }, [storedCart, storageKey]);

  // Latest cart, readable from a callback that outlives the render it was made
  // in. applyCartOptimization runs after a fetch and must act on the cart as it
  // is when it lands, not as it was when the shopper pressed the button. It is
  // the stored cart, because what these callbacks return is stored.
  const cartRef = useRef(storedCart);
  useEffect(() => { cartRef.current = storedCart; }, [storedCart]);

  /**
   * Add item to cart
   * For sell mode: standard behavior
   * For buy mode: includes buylist pricing and payment type
   */
  // Helper: check if two cart entries represent the same logical item.
  // For buy mode, uniqueness = productId + variant + paymentType.
  // For sell mode, uniqueness = the variant.
  //
  // This used to compare `skuId` alone, on the belief that a skuId was "already
  // globally unique from variant system". It is not: the server now serves one
  // option per (skuId, condAbbr, printing, langAbbr), because a graded slab
  // shares the raw card's skuId. Comparing skuId here would merge a $102 slab
  // line and a $5 raw line into one, charging whichever price landed first for
  // both. TKT-2026-105367.
  const isSameItem = useCallback((a, b) => {
    if (a.mode !== b.mode) return false;
    if (!sameVariant(a.variant, b.variant)) return false;
    if (a.mode === 'buy') {
      return a.productId === b.productId && a.paymentType === b.paymentType;
    }
    return true;
  }, []);

  const addToCart = useCallback((item) => {
    setCart(prev => {
      // One cert number is one slab: the same slab again, on either payment
      // leg, is not a second copy (TKT-2026-107322).
      if (item.mode === 'buy' && item.variant?.certNumber && prev.some(p =>
        p.mode === 'buy' && p.productId === item.productId && sameVariant(p.variant, item.variant))) {
        return prev;
      }

      const exists = prev.find(p => isSameItem(p, item));

      if (exists) {
        // For buy mode, we don't need to cap quantity (we're buying from customer)
        if (item.mode === 'buy') {
          return prev.map(p =>
            isSameItem(p, item)
              ? { ...p, qty: p.qty + (item.qty || 1) }
              : p
          );
        }

        // For sell mode, cap at available stock, and at the store's order size
        // limit (TKT-2026-105480).
        const room = roomFor(prev, item, orderLimitsRef.current);
        const newQty = Math.min(exists.qty + Math.min(item.qty || 1, room), item.variant.quantity);
        if (newQty === exists.qty) return prev;
        return prev.map(p =>
          isSameItem(p, item)
            ? { ...p, qty: newQty }
            : p
        );
      }

      // New item
      if (item.mode === 'sell' && item.variant.quantity < 1) {
        // Nothing to add if out of stock in sell mode
        return prev;
      }

      const initialQty = item.mode === 'buy'
        ? (item.qty || 1)
        : Math.min(item.qty || 1, item.variant.quantity, roomFor(prev, item, orderLimitsRef.current));

      // The order is already at the store's limit. The cart says so, and names
      // where a larger order goes. TKT-2026-105480
      if (initialQty < 1) return prev;

      return [...prev, {
        ...item,
        qty: initialQty,
        mode: item.mode || 'sell',
        paymentType: item.paymentType || 'Cash',
        buylistCashPrice: item.buylistCashPrice || 0,
        buylistCreditPrice: item.buylistCreditPrice || 0
      }];
    });
  }, [isSameItem]);

  /**
   * Update quantity for a cart item.
   *
   * `key` is a variantKey(), not a bare skuId: one skuId can now be two different
   * cards on the same screen (a slab and a raw copy), and targeting by skuId
   * would move both lines at once. TKT-2026-105367.
   *
   * For buy mode, pass productId and paymentType so we target the correct line.
   */
  const updateQuantity = useCallback((key, qty, mode = 'sell', productId = null, paymentType = null) => {
    setCart(prev =>
      prev.map(p => {
        if (variantKey(p.variant) !== key || p.mode !== mode) return p;
        // For buy mode, also match productId and paymentType
        if (mode === 'buy') {
          if (productId != null && p.productId !== productId) return p;
          if (paymentType != null && p.paymentType !== paymentType) return p;
          return { ...p, qty: Math.max(1, qty) };
        }
        // For sell mode, cap at stock. An increase is also capped at the store's
        // order size limit; a decrease always goes through. TKT-2026-105480
        const wanted = Math.min(Math.max(1, qty), p.variant.quantity);
        if (wanted <= p.qty) return { ...p, qty: wanted };
        const room = roomFor(prev, p, orderLimitsRef.current);
        return { ...p, qty: Math.min(wanted, p.qty + room) };
      })
    );
  }, []);

  /**
   * Update payment type for a buy mode item.
   * Each productId + skuId + paymentType combo is unique, so changing payment
   * type effectively creates a new logical entry. We handle this by removing
   * the old entry and adding a new one with the updated payment type.
   */
  const updatePaymentType = useCallback((key, newPaymentType, productId = null, oldPaymentType = null) => {
    setCart(prev =>
      prev.map(p => {
        if (variantKey(p.variant) !== key || p.mode !== 'buy') return p;
        if (productId != null && p.productId !== productId) return p;
        if (oldPaymentType != null && p.paymentType !== oldPaymentType) return p;
        return { ...p, paymentType: newPaymentType };
      })
    );
  }, []);

  /**
   * Remove item from cart.
   * For buy mode, pass productId and paymentType so only the exact line is removed.
   */
  const removeFromCart = useCallback((key, mode = 'sell', productId = null, paymentType = null) => {
    setCart(prev => prev.filter(p => {
      if (variantKey(p.variant) !== key || p.mode !== mode) return true; // keep
      // For buy mode, also check productId and paymentType
      if (mode === 'buy') {
        if (productId != null && p.productId !== productId) return true;
        if (paymentType != null && p.paymentType !== paymentType) return true;
      }
      return false; // remove this match
    }));
  }, []);

  /**
   * Clear entire cart
   */
  const clearCart = useCallback(() => {
    setCart([]);
    // The cart is cleared when a transaction finishes, which means the next
    // person at the kiosk starts from the store's own defaults, not the last
    // shopper's condition floor. TKT-2026-105225
    setCartPreferences(DEFAULT_PREFERENCES);
  }, []);

  /**
   * Set one axis of the cart optimisation preference (TKT-2026-105225).
   * Changing a preference does not by itself re-pick anything: the shopper
   * applies it explicitly, so a stray tap never silently reprices their cart.
   */
  const setCartPreference = useCallback((key, value) => {
    setCartPreferences(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetCartPreferences = useCallback(() => setCartPreferences(DEFAULT_PREFERENCES), []);

  /**
   * Re-pick which copy every sell line buys, against variants just read from the
   * server, and return what changed so the UI can say so.
   *
   * Buy-mode lines are left exactly where they are: a buylist line is an offer on
   * the printing and condition the shopper brought in, not a copy we get to
   * choose. They are appended after the sell lines because the cart renders the
   * two modes in separate sections, so their position in this array is not what
   * orders them on screen.
   *
   * @param {Object} variantsByProductId productId -> live in-store variants
   * @param {Object} prefs               the preference to apply
   * @returns {Object} summary { switched, merged, reduced, blocked, priceDelta }
   */
  const applyCartOptimization = useCallback((variantsByProductId, prefs) => {
    // Read through the ref, not the render-time `cart`. Applying happens after a
    // network round trip, and a shopper can change a quantity while it is in
    // flight; closing over the older array would silently undo that.
    const current = cartRef.current;
    const isSell = item => (item?.mode || 'sell') === 'sell';
    const sellItems = current.filter(isSell);
    const otherItems = current.filter(item => !isSell(item));
    const { items, summary } = optimizeCart(sellItems, variantsByProductId, prefs);
    // Re-picking copies can split a line across two SKUs. If that would take an
    // order that fits the store's limit past it, the cart is left as it was.
    // TKT-2026-105480
    const limits = orderLimitsRef.current;
    const before = orderLimitStatus(sellItems, limits);
    const after = orderLimitStatus(items, limits);
    if (after.over && !before.over) {
      return { ...summary, switched: 0, merged: 0, blocked: [], reduced: [], priceDelta: 0, orderLimit: true };
    }
    setCart([...items, ...otherItems]);
    return summary;
  }, []);

  /**
   * Put the store's current offer on the trade-in lines the buylist refused
   * (TKT-2026-106839) and return what changed so the checkout can say so.
   * Read through the ref for the same reason as applyCartOptimization: this runs
   * when the refusal lands, not when the shopper pressed Confirm.
   */
  const applyBuylistRefusal = useCallback((violations) => {
    const result = applyBuylistPriceRefusal(cartRef.current, violations, currencyCode);
    setCart(result.cart);
    return result;
  }, [currencyCode]);

  /**
   * Put what the buylist BOOKED on each trade-in line once the order is placed
   * (TKT-2026-107051), so the receipt prints the order's money rather than the
   * kiosk's own derivation of it. `buyLines` is the kiosk backend's answer, one
   * per buy line in the order they were sent. A booked line is final, so its
   * ladder is dropped and nothing re-walks it.
   */
  const applyBookedBuyLines = useCallback((buyLines) => {
    if (!Array.isArray(buyLines) || buyLines.length === 0) return;
    setCart(prev => {
      let i = 0;
      return prev.map(item => {
        if (item.mode !== 'buy') return item;
        const booked = buyLines[i++];
        if (!booked || String(booked.productId) !== String(item.productId)) return item;
        return {
          ...item,
          paymentType: booked.paymentType || item.paymentType,
          buylistCashPrice: Number(booked.buylistCashPrice) || 0,
          buylistCreditPrice: Number(booked.buylistCreditPrice) || 0,
          buylistLadder: null,
        };
      });
    });
  }, []);

  /**
   * Request an idle reset. Returns false (and does nothing) while a transaction is
   * in progress so we never wipe an in-flight checkout or the just-issued pickup
   * code. Otherwise clears the cart and bumps resetNonce. (TKT-2026-103353)
   */
  const requestReset = useCallback(() => {
    if (transactionInProgress) return false;
    setCart([]);
    setCartPreferences(DEFAULT_PREFERENCES);
    setResetNonce((n) => n + 1);
    return true;
  }, [transactionInProgress]);

  /**
   * Clear only sell mode items
   */
  const clearSellCart = useCallback(() => {
    setCart(prev => prev.filter(p => p.mode !== 'sell'));
  }, []);

  /**
   * Clear only buy mode items
   */
  const clearBuyCart = useCallback(() => {
    setCart(prev => prev.filter(p => p.mode !== 'buy'));
  }, []);

  // Filtered cart items by mode
  const sellCartItems = useMemo(() =>
    cart.filter(item => item.mode === 'sell'),
    [cart]
  );

  const buyCartItems = useMemo(() =>
    cart.filter(item => item.mode === 'buy'),
    [cart]
  );

  // Where the cart stands against the store's order size limit (TKT-2026-105480)
  const orderLimit = useMemo(() => orderLimitStatus(cart, orderLimits), [cart, orderLimits]);

  // Sell mode totals
  const sellTotal = useMemo(() =>
    sellCartItems.reduce((sum, item) => sum + (item.price * item.qty), 0),
    [sellCartItems]
  );

  // Buy mode totals (separated by payment type)
  const buyTotalCash = useMemo(() =>
    buyCartItems
      .filter(item => item.paymentType === 'Cash')
      .reduce((sum, item) => sum + ((item.buylistCashPrice || 0) * item.qty), 0),
    [buyCartItems]
  );

  const buyTotalCredit = useMemo(() =>
    buyCartItems
      .filter(item => item.paymentType === 'Credit')
      .reduce((sum, item) => sum + ((item.buylistCreditPrice || 0) * item.qty), 0),
    [buyCartItems]
  );

  // Total buy value (using each item's selected payment type price)
  const buyTotal = useMemo(() =>
    buyCartItems.reduce((sum, item) => {
      const price = item.paymentType === 'Credit'
        ? (item.buylistCreditPrice || 0)
        : (item.buylistCashPrice || 0);
      return sum + (price * item.qty);
    }, 0),
    [buyCartItems]
  );

  return (
    <CartContext.Provider
      value={{
        // Cart state
        cart,
        kioskMode,
        setKioskMode,

        // Idle auto-reset (TKT-2026-103353)
        transactionInProgress,
        setTransactionInProgress,
        resetNonce,
        requestReset,

        // Cart actions
        addToCart,
        updateQuantity,
        updatePaymentType,
        removeFromCart,
        clearCart,
        clearSellCart,
        clearBuyCart,

        // Cart optimisation (TKT-2026-105225)
        cartPreferences,
        setCartPreference,
        resetCartPreferences,
        applyCartOptimization,

        // Buylist refusal (TKT-2026-106839)
        applyBuylistRefusal,
        // What the buylist booked (TKT-2026-107051)
        applyBookedBuyLines,

        // Order size limit (TKT-2026-105480)
        orderLimit,

        // Filtered items
        sellCartItems,
        buyCartItems,

        // Totals
        sellTotal,
        buyTotal,
        buyTotalCash,
        buyTotalCredit
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
}

